import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Paper, Button, IconButton, Chip, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
  Collapse, Avatar, Dialog, DialogContent, Snackbar,
  InputAdornment, TextField, Skeleton, useTheme
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import VerifiedIcon from '@mui/icons-material/Verified'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SpeedIcon from '@mui/icons-material/Speed'
import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import BusinessIcon from '@mui/icons-material/Business'
import DescriptionIcon from '@mui/icons-material/Description'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import FavoriteIcon from '@mui/icons-material/Favorite'
import LightbulbIcon from '@mui/icons-material/Lightbulb'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import LanguageIcon from '@mui/icons-material/Language'
import EmailIcon from '@mui/icons-material/Email'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import AddIcon from '@mui/icons-material/Add'
import SyncIcon from '@mui/icons-material/Sync'
import ReplayIcon from '@mui/icons-material/Replay'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions'
import anime from 'animejs'
import moment from 'moment'
import 'moment/locale/pt-br'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import { TemplatePreviewBubble, findComponent } from '../TemplatePreviewBubble'
import { mergeTemplates, canRetry, canEdit, isOrphanFromAnotherWaba, statusInfo, describeApiError, componentsToForm, TEMPLATE_CATEGORY_LABELS } from '../../libs/metaTemplates'
import RecommendedTemplatesDialog from './metaTemplates/RecommendedTemplatesDialog'
import CreateTemplateDialog from './metaTemplates/CreateTemplateDialog'
import HowToUseTemplateDialog from './metaTemplates/HowToUseTemplateDialog'
import ConfirmationModal from '../ConfirmationModal'
import MetaAccountHealthBanner from '../MetaAccountHealthBanner'

moment.locale('pt-br')

// A Meta API as vezes devolve em UPPER, as vezes em lower, as vezes prefixado.
// Normalizamos pra uppercase e tiramos prefixos comuns antes de buscar no mapa.
function normalizeStatus(value) {
  if (value == null) return null
  const upper = String(value).toUpperCase().trim()
  if (!upper) return null
  return upper
}

function normalizeTier(value) {
  if (value == null) return null
  let upper = String(value).toUpperCase().trim()
  if (!upper) return null
  // alguns retornos vem com prefixo MESSAGING_LIMIT_ ou similar
  upper = upper.replace(/^MESSAGING_LIMIT_/, '')
  if (!upper.startsWith('TIER_')) {
    upper = `TIER_${upper}`
  }
  return upper
}

const QUALITY_COLORS = {
  GREEN: '#22c55e',
  YELLOW: '#eab308',
  RED: '#ef4444',
  UNKNOWN: '#94a3b8',
}

const QUALITY_LABELS = {
  GREEN: 'Excelente',
  YELLOW: 'Atenção',
  RED: 'Crítica',
  UNKNOWN: 'Sem dados',
}

const QUALITY_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Qualidade do número (avaliada pela Meta)</div>
    <div className="mt-1">🟢 <b>Excelente:</b> clientes satisfeitos. Você está liberado para enviar normalmente, sem restrições.</div>
    <div className="mt-1">🟡 <b>Atenção:</b> alguns clientes marcaram suas mensagens como indesejadas. Reduza marketing, melhore relevância e evite envios em massa. Se piorar, vai para Crítica.</div>
    <div className="mt-1">🔴 <b>Crítica:</b> a Meta pode limitar ou suspender seu envio em breve. Pare campanhas e foque apenas em mensagens úteis. Recuperar leva semanas.</div>
    <div className="mt-1">⚪ <b>Sem dados:</b> volume insuficiente ou número novo. A nota aparece depois de algumas centenas de mensagens enviadas.</div>
  </div>
)

const TIER_LABELS = {
  TIER_50: 'Nível 50',
  TIER_250: 'Nível 250',
  TIER_1K: 'Nível 1K',
  TIER_10K: 'Nível 10K',
  TIER_100K: 'Nível 100K',
  TIER_UNLIMITED: 'Sem limite',
}

const TIER_DESCRIPTIONS = {
  TIER_50: 'até 50 conversas iniciadas por você em 24h',
  TIER_250: 'até 250 conversas iniciadas por você em 24h',
  TIER_1K: 'até 1.000 conversas iniciadas por você em 24h',
  TIER_10K: 'até 10.000 conversas iniciadas por você em 24h',
  TIER_100K: 'até 100.000 conversas iniciadas por você em 24h',
  TIER_UNLIMITED: 'sem limite de conversas iniciadas',
}

const TIER_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Limite de conversas iniciadas por dia</div>
    <div className="mt-1">A Meta promove automaticamente seu número conforme o uso e a qualidade aumentam:</div>
    <div className="mt-1">50 → 250 → 1.000 → 10.000 → 100.000 → Sem limite</div>
    <div className="mt-2"><b>Para subir de nível:</b> mantenha a qualidade Excelente, envie consistentemente e chegue perto do limite atual por alguns dias. A promoção acontece sozinha em 24-48h.</div>
    <div className="mt-2"><i>Conversas iniciadas pelo cliente (resposta a uma mensagem do cliente) não contam pro limite.</i></div>
  </div>
)

const BUSINESS_VERIFICATION_LABELS = {
  VERIFIED: 'Verificada',
  NOT_VERIFIED: 'Não verificada',
  PENDING_NEED_MORE_INFO: 'Faltam informações',
  PENDING: 'Em análise',
  REJECTED: 'Rejeitada',
  EXPIRED: 'Expirada',
}

const BUSINESS_VERIFICATION_COLORS = {
  VERIFIED: QUALITY_COLORS.GREEN,
  NOT_VERIFIED: QUALITY_COLORS.UNKNOWN,
  PENDING_NEED_MORE_INFO: QUALITY_COLORS.YELLOW,
  PENDING: QUALITY_COLORS.YELLOW,
  REJECTED: QUALITY_COLORS.RED,
  EXPIRED: QUALITY_COLORS.RED,
}

const BUSINESS_VERIFICATION_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Verificação do negócio pela Meta</div>
    <div className="mt-1">✅ <b>Verificada:</b> empresa validada pela Meta com documentos oficiais. Dá acesso completo aos recursos avançados e mais credibilidade.</div>
    <div className="mt-1">⚪ <b>Não verificada:</b> conta operacional, mas não passou pela verificação. Funciona normalmente; verificar é opcional para uso comum.</div>
    <div className="mt-1">🟡 <b>Em análise:</b> documentos enviados, aguardando análise. Costuma levar até 14 dias úteis.</div>
    <div className="mt-1">🟡 <b>Faltam informações:</b> a Meta pediu documentos adicionais. Acesse o Business Manager.</div>
    <div className="mt-1">🔴 <b>Rejeitada/Expirada:</b> pode reenviar com documentos atualizados pelo Business Manager.</div>
  </div>
)

const NAME_STATUS_LABELS = {
  APPROVED: 'Nome aprovado',
  AVAILABLE_WITHOUT_REVIEW: 'Disponível',
  DECLINED: 'Nome recusado',
  EXPIRED: 'Nome expirado',
  PENDING_REVIEW: 'Nome em revisão',
  NONE: 'Sem nome',
}

const NAME_STATUS_COLORS = {
  APPROVED: QUALITY_COLORS.GREEN,
  AVAILABLE_WITHOUT_REVIEW: QUALITY_COLORS.GREEN,
  PENDING_REVIEW: QUALITY_COLORS.YELLOW,
  DECLINED: QUALITY_COLORS.RED,
  EXPIRED: QUALITY_COLORS.RED,
  NONE: QUALITY_COLORS.UNKNOWN,
}

const NAME_STATUS_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Status do nome verificado</div>
    <div className="mt-1">✅ <b>Aprovado:</b> nome aparece como nome oficial da empresa nos chats com seus clientes.</div>
    <div className="mt-1">✅ <b>Disponível:</b> nome registrado e disponível para uso sem necessidade de revisão.</div>
    <div className="mt-1">🟡 <b>Em revisão:</b> Meta está analisando. Costuma levar até 48 horas.</div>
    <div className="mt-1">🔴 <b>Recusado:</b> tente um nome mais alinhado com sua empresa (use o nome registrado, não fantasia).</div>
    <div className="mt-1">🔴 <b>Expirado:</b> renove pelo Business Manager.</div>
    <div className="mt-1">⚪ <b>Sem nome:</b> nenhum nome registrado. Configure em Business Manager → WhatsApp Manager.</div>
  </div>
)

const CODE_VERIFICATION_LABELS = {
  VERIFIED: 'PIN ativo',
  NOT_VERIFIED: 'PIN pendente',
  EXPIRED: 'PIN não renovado',
}

const CODE_VERIFICATION_COLORS = {
  VERIFIED: QUALITY_COLORS.GREEN,
  NOT_VERIFIED: QUALITY_COLORS.YELLOW,
  EXPIRED: QUALITY_COLORS.YELLOW,
}

const CODE_VERIFICATION_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">PIN de verificação em duas etapas</div>
    <div className="mt-1">Este é o PIN que protege a re-registro do seu número no WhatsApp Business Platform. Ele <b>não afeta o funcionamento do envio</b> — seu número continua ativo mesmo sem PIN.</div>
    <div className="mt-2">✅ <b>PIN ativo:</b> camada extra de segurança configurada.</div>
    <div className="mt-1">🟡 <b>PIN pendente:</b> não foi configurado ainda. Recomendado, mas opcional.</div>
    <div className="mt-1">🟡 <b>PIN não renovado:</b> o PIN expirou. Renove pelo Business Manager quando puder — sem urgência.</div>
  </div>
)

const TEMPLATE_STATUS_LABELS = {
  APPROVED: 'Aprovado',
  PENDING: 'Em análise',
  IN_APPEAL: 'Em recurso',
  REJECTED: 'Rejeitado',
  DISABLED: 'Desativado',
  PAUSED: 'Pausado',
  LIMIT_EXCEEDED: 'Limite excedido',
  FLAGGED: 'Sinalizado',
  NOT_FOUND: 'Não encontrado na Meta',
}

const TEMPLATE_STATUS_COLORS = {
  APPROVED: QUALITY_COLORS.GREEN,
  PENDING: QUALITY_COLORS.YELLOW,
  IN_APPEAL: QUALITY_COLORS.YELLOW,
  REJECTED: QUALITY_COLORS.RED,
  DISABLED: QUALITY_COLORS.UNKNOWN,
  PAUSED: '#f97316',
  LIMIT_EXCEEDED: QUALITY_COLORS.RED,
  FLAGGED: QUALITY_COLORS.RED,
  NOT_FOUND: QUALITY_COLORS.UNKNOWN,
}

const TEMPLATES_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Modelos de mensagem pré-aprovados</div>
    <div className="mt-1">Templates são mensagens que precisam de aprovação da Meta antes de poder iniciar conversas com clientes.</div>
    <div className="mt-2">✅ <b>Aprovado:</b> pronto para uso em campanhas e notificações.</div>
    <div className="mt-1">🟡 <b>Em análise:</b> aguardando aprovação da Meta (1-24h normalmente).</div>
    <div className="mt-1">🔴 <b>Rejeitado:</b> precisa ajustar e reenviar.</div>
    <div className="mt-1">🟠 <b>Pausado:</b> temporariamente desabilitado pela Meta por má performance.</div>
    <div className="mt-2"><b>Importante:</b> templates aprovados são obrigatórios para iniciar conversas com clientes (24h depois da última resposta do cliente).</div>
  </div>
)

const NUMBER_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Número conectado à Meta Oficial</div>
    <div className="mt-1">Este é o número de telefone que aparece para seus clientes quando você envia mensagens via API Oficial. Diferente do QR Code, este número está vinculado à infraestrutura oficial do WhatsApp Business Platform.</div>
  </div>
)

const HEALTH_SCORE_TOOLTIP = (
  <div className="text-xs leading-relaxed">
    <div className="font-bold mb-1">Saúde geral da conta</div>
    <div className="mt-1">Pontuação consolidada calculada a partir de qualidade do número, verificação do negócio, status do nome e verificação do número.</div>
    <div className="mt-2"><b>0-39:</b> Crítico — risco real de limitação ou suspensão</div>
    <div className="mt-1"><b>40-69:</b> Regular — alguns pontos precisam de atenção</div>
    <div className="mt-1"><b>70-89:</b> Bom — operação saudável</div>
    <div className="mt-1"><b>90-100:</b> Excelente — conta plenamente otimizada</div>
  </div>
)

const VERTICAL_LABELS = {
  AUTO: 'Automotivo',
  BEAUTY: 'Beleza, spa e bem-estar',
  APPAREL: 'Vestuário',
  EDU: 'Educação',
  ENTERTAIN: 'Entretenimento',
  EVENT_PLAN: 'Eventos',
  FINANCE: 'Finanças',
  GROCERY: 'Mercado',
  GOVT: 'Governo',
  HOTEL: 'Hotelaria',
  HEALTH: 'Saúde',
  NONPROFIT: 'Sem fins lucrativos',
  PROF_SERVICES: 'Serviços profissionais',
  RETAIL: 'Varejo',
  TRAVEL: 'Viagem',
  RESTAURANT: 'Restaurante',
  OTHER: 'Outro',
  UNDEFINED: 'Não definido',
}

const SCORE_WEIGHTS = {
  quality: 40,
  business: 20,
  name: 20,
  code: 20,
}

function computeHealthScore({ phoneNumber, waba }) {
  let total = 0
  const factors = []

  const qualityMap = { GREEN: 1, YELLOW: 0.5, RED: 0, UNKNOWN: 0.6 }
  const qualityKey = normalizeStatus(phoneNumber?.quality_rating)
  const qualityFactor = qualityMap[qualityKey] != null ? qualityMap[qualityKey] : 0.6
  total += qualityFactor * SCORE_WEIGHTS.quality
  factors.push({ label: 'Qualidade do número', value: Math.round(qualityFactor * 100), weight: SCORE_WEIGHTS.quality })

  const businessMap = { VERIFIED: 1, PENDING: 0.5, PENDING_NEED_MORE_INFO: 0.3, NOT_VERIFIED: 0.4, REJECTED: 0, EXPIRED: 0 }
  const businessKey = normalizeStatus(waba?.business_verification_status)
  const businessFactor = businessMap[businessKey] != null ? businessMap[businessKey] : 0.4
  total += businessFactor * SCORE_WEIGHTS.business
  factors.push({ label: 'Verificação do negócio', value: Math.round(businessFactor * 100), weight: SCORE_WEIGHTS.business })

  const nameMap = { APPROVED: 1, AVAILABLE_WITHOUT_REVIEW: 0.8, PENDING_REVIEW: 0.5, DECLINED: 0.2, EXPIRED: 0.2, NONE: 0.3 }
  const nameKey = normalizeStatus(phoneNumber?.name_status)
  const nameFactor = nameMap[nameKey] != null ? nameMap[nameKey] : 0.3
  total += nameFactor * SCORE_WEIGHTS.name
  factors.push({ label: 'Status do nome', value: Math.round(nameFactor * 100), weight: SCORE_WEIGHTS.name })

  // PIN de verificação em 2 etapas — opcional, não bloqueia envio.
  // EXPIRED ainda pontua razoavelmente bem (0.7) porque é um item de segurança extra.
  const codeMap = { VERIFIED: 1, NOT_VERIFIED: 0.7, EXPIRED: 0.7 }
  const codeKey = normalizeStatus(phoneNumber?.code_verification_status)
  const codeFactor = codeMap[codeKey] != null ? codeMap[codeKey] : 0.7
  total += codeFactor * SCORE_WEIGHTS.code
  factors.push({ label: 'PIN de segurança', value: Math.round(codeFactor * 100), weight: SCORE_WEIGHTS.code })

  return { score: Math.round(total), factors }
}

function getHealthLabel(score) {
  if (score >= 90) return { label: 'Excelente', color: QUALITY_COLORS.GREEN, description: 'Sua conta está plenamente otimizada. Continue assim.' }
  if (score >= 70) return { label: 'Bom', color: QUALITY_COLORS.GREEN, description: 'Operação saudável. Há pontos de melhoria, mas nada urgente.' }
  if (score >= 40) return { label: 'Regular', color: QUALITY_COLORS.YELLOW, description: 'Alguns pontos precisam de atenção para evitar restrições futuras.' }
  return { label: 'Crítico', color: QUALITY_COLORS.RED, description: 'Risco real de limitação ou suspensão. Veja os próximos passos abaixo.' }
}

function computeNextSteps({ phoneNumber, waba, templates }) {
  const steps = []

  const qualityKey = normalizeStatus(phoneNumber?.quality_rating)
  const businessKey = normalizeStatus(waba?.business_verification_status)
  const nameKey = normalizeStatus(phoneNumber?.name_status)
  const codeKey = normalizeStatus(phoneNumber?.code_verification_status)

  if (qualityKey === 'RED') {
    steps.push({
      severity: 'high',
      title: 'Reduza envios e melhore a qualidade',
      description: 'Sua qualidade está crítica. Pare campanhas de marketing imediatamente, foque apenas em mensagens transacionais (cobranças, notificações de pedido) e reduza o volume diário por 1-2 semanas.',
    })
  } else if (qualityKey === 'YELLOW') {
    steps.push({
      severity: 'medium',
      title: 'Evite cair para a qualidade Crítica',
      description: 'Clientes estão reportando suas mensagens. Reveja os templates ativos, evite envios em massa para listas frias e priorize mensagens relevantes.',
    })
  }

  if (!businessKey || businessKey === 'NOT_VERIFIED') {
    steps.push({
      severity: 'low',
      title: 'Verifique seu negócio na Meta',
      description: 'A verificação dá mais credibilidade e abre caminho para limites maiores de envio e para pedir o selo de verificação. Acesse o Business Manager para enviar os documentos.',
    })
  }

  if (nameKey === 'NONE' || !nameKey) {
    steps.push({
      severity: 'medium',
      title: 'Cadastre um nome verificado',
      description: 'Sem nome verificado, seus clientes veem apenas o número de telefone nos chats. Cadastre o nome da sua empresa em Business Manager → WhatsApp Manager.',
    })
  } else if (nameKey === 'DECLINED') {
    steps.push({
      severity: 'high',
      title: 'Reenvie o nome verificado',
      description: 'A Meta recusou o nome registrado. Tente novamente com o nome registrado da empresa (não o nome fantasia), em letras simples e sem caracteres especiais.',
    })
  }

  if (codeKey === 'EXPIRED') {
    steps.push({
      severity: 'low',
      title: 'Renove o PIN de duas etapas (opcional)',
      description: 'O PIN de proteção do registro expirou. Não afeta os envios, mas é recomendado renovar pelo Business Manager para manter a camada extra de segurança.',
    })
  } else if (codeKey === 'NOT_VERIFIED') {
    steps.push({
      severity: 'low',
      title: 'Configure o PIN de duas etapas (opcional)',
      description: 'Adicione um PIN no Business Manager para evitar que outra pessoa re-registre seu número. Recomendado por segurança, mas não é obrigatório.',
    })
  }

  const approvedTemplates = templates.filter(t => t.status === 'APPROVED').length
  if (approvedTemplates === 0) {
    steps.push({
      severity: 'medium',
      title: 'Crie seu primeiro template aprovado',
      description: 'Templates são obrigatórios para iniciar conversas com clientes que não responderam nas últimas 24 horas. Sem nenhum aprovado, você só consegue responder mensagens recebidas.',
    })
  }

  const pendingTemplates = templates.filter(t => t.status === 'PENDING').length
  if (pendingTemplates > 0) {
    steps.push({
      severity: 'info',
      title: `${pendingTemplates} template(s) aguardando aprovação`,
      description: 'A Meta normalmente aprova ou rejeita em 1-24 horas. Você será notificado quando o status mudar.',
    })
  }

  const rejectedTemplates = templates.filter(t => t.status === 'REJECTED').length
  if (rejectedTemplates > 0) {
    steps.push({
      severity: 'medium',
      title: `${rejectedTemplates} template(s) rejeitado(s)`,
      description: 'Templates rejeitados não podem ser usados. Revise os motivos no Business Manager e crie versões ajustadas.',
    })
  }

  return steps
}

// findComponent, renderTemplateText, BUTTON_ICONS e TemplatePreviewBubble foram
// extraídos para components/TemplatePreviewBubble (reuso pela tela de Relatórios).

function TemplatePreviewDialog({ template, open, onClose, onRefresh }) {
  if (!template) return null
  const components = Array.isArray(template.components) ? template.components : []
  const hasComponents = components.length > 0
  const bodyComponent = findComponent(components, 'BODY')
  const placeholders = (bodyComponent?.text?.match(/\{\{\d+\}\}/g) || []).length

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#0b141a' } }}
    >
      <div className="flex items-center justify-between px-4 py-3" style={{ backgroundColor: '#202c33' }}>
        <div className="flex-1 min-w-0">
          <Text className="font-black text-sm truncate text-white">{template.name}</Text>
          <Text className="text-xs font-light text-gray-400 mt-0.5">
            {template.language} • {TEMPLATE_CATEGORY_LABELS[normalizeStatus(template.category)] || template.category || '—'}
          </Text>
        </div>
        <IconButton size="small" onClick={onClose} sx={{ color: '#aebac1' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </div>

      <DialogContent
        sx={{
          backgroundColor: '#0b141a',
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          padding: '24px 16px',
        }}
      >
        {hasComponents ? (
          <>
            <div className="flex justify-start">
              <TemplatePreviewBubble template={template} />
            </div>
            {placeholders > 0 && (
              <div className="mt-4 mx-auto max-w-[340px] px-3 py-2 rounded-md" style={{ backgroundColor: 'rgba(37, 211, 102, 0.12)' }}>
                <Text className="text-xs font-light" style={{ color: '#aebac1' }}>
                  <b style={{ color: '#25d366' }}>{placeholders}</b> {placeholders === 1 ? 'variável' : 'variáveis'} dinâmicas no corpo da mensagem. Esses campos são preenchidos pelo seu sistema na hora do envio.
                </Text>
              </div>
            )}
          </>
        ) : (
          <div className="mx-auto max-w-[340px] text-center py-6">
            <ErrorOutlineIcon style={{ fontSize: 36, color: '#aebac1', opacity: 0.6 }} />
            <Text className="text-sm font-bold mt-2 text-white">
              Conteúdo ainda não sincronizado
            </Text>
            <Text className="text-xs font-light mt-1" style={{ color: '#aebac1' }}>
              Este modelo já foi cadastrado, mas o painel ainda não baixou o corpo da mensagem dele. Clique em <b>Sincronizar agora</b> no topo do painel para forçar uma atualização.
            </Text>
            {onRefresh && (
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => { onClose(); onRefresh(); }}
                sx={{ mt: 2, backgroundColor: '#25d366', '&:hover': { backgroundColor: '#1ea854' } }}
              >
                Sincronizar agora
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function StatusPill({ color, label, tooltip }) {
  const pill = (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold"
      style={{ backgroundColor: `${color}1F`, color }}
    >
      <span
        className="w-[8px] h-[8px] rounded-full mr-1.5"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  )
  if (!tooltip) return pill
  return <Tooltip title={tooltip} arrow placement="top">{pill}</Tooltip>
}

function CardTitle({ icon: Icon, label, color, tooltip }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon style={{ color: color || 'inherit', fontSize: 20 }} />
      <Text className="text-xs font-bold uppercase tracking-wider opacity-70 flex-1">
        {label}
      </Text>
      {tooltip && (
        <Tooltip title={tooltip} arrow placement="top">
          <HelpOutlineIcon style={{ fontSize: 16, opacity: 0.5, cursor: 'help' }} />
        </Tooltip>
      )}
    </div>
  )
}

function ScoreRing({ score, color, size = 96 }) {
  const stroke = 8
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (score / 100) * circumference

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(128,128,128,0.18)"
          strokeWidth={stroke}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Text className="text-2xl font-black" style={{ color }}>
          <AnimatedNumber value={score} />
        </Text>
        <Text className="text-[10px] font-light opacity-70 -mt-1">de 100</Text>
      </div>
    </div>
  )
}

function useAnimatedNumber(target, duration = 900) {
  const [value, setValue] = useState(0)
  const startRef = useRef(null)
  const fromRef = useRef(0)
  useEffect(() => {
    if (target == null || Number.isNaN(target)) return
    fromRef.current = value
    startRef.current = null
    const tick = (ts) => {
      if (startRef.current == null) startRef.current = ts
      const elapsed = ts - startRef.current
      const progress = Math.min(elapsed / duration, 1)
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3)
      const next = fromRef.current + (target - fromRef.current) * eased
      setValue(Math.round(next))
      if (progress < 1) requestAnimationFrame(tick)
      else setValue(target)
    }
    const handle = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, duration])
  return value
}

function AnimatedNumber({ value, className, style }) {
  const animated = useAnimatedNumber(value)
  return <span className={className} style={style}>{animated}</span>
}

function SnapshotSkeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="space-y-2">
          <Skeleton variant="text" width={240} height={28} />
          <Skeleton variant="text" width={180} height={14} />
        </div>
        <Skeleton variant="rounded" width={150} height={32} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Paper elevation={3} sx={{ p: 2.5 }}>
          <Skeleton variant="text" width={130} height={16} />
          <div className="flex items-center gap-3 mt-2">
            <Skeleton variant="circular" width={96} height={96} />
            <div className="flex-1 space-y-2">
              <Skeleton variant="text" width="60%" height={22} />
              <Skeleton variant="text" width="90%" height={14} />
              <Skeleton variant="text" width="80%" height={14} />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton variant="text" width="40%" height={12} />
                <Skeleton variant="rounded" width={64} height={6} />
                <Skeleton variant="text" width={24} height={12} />
              </div>
            ))}
          </div>
        </Paper>

        <div className="md:col-span-2">
          <Paper elevation={3} sx={{ p: 2.5, height: '100%' }}>
            <Skeleton variant="text" width={140} height={16} />
            <div className="flex items-start gap-4 mt-2">
              <Skeleton variant="circular" width={72} height={72} />
              <div className="flex-1 space-y-2">
                <Skeleton variant="text" width="50%" height={22} />
                <Skeleton variant="text" width="35%" height={14} />
                <Skeleton variant="text" width="90%" height={14} />
                <Skeleton variant="text" width="70%" height={14} />
              </div>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {[0, 1, 2, 3].map(i => (
                <Skeleton key={i} variant="text" width="80%" height={14} />
              ))}
            </div>
          </Paper>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2, 3, 4, 5].map(i => (
          <Paper key={i} elevation={2} sx={{ p: 2.5 }}>
            <Skeleton variant="text" width="55%" height={14} />
            <Skeleton variant="text" width="75%" height={26} sx={{ mt: 0.5 }} />
            <Skeleton variant="text" width="90%" height={14} sx={{ mt: 1 }} />
          </Paper>
        ))}
      </div>
    </div>
  )
}

function MetaWabaSnapshot({ api, secret, connectionSlot }) {
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [response, setResponse] = useState(null)
  const [error, setError] = useState(null)
  const [templatesExpanded, setTemplatesExpanded] = useState(false)
  const [previewTemplate, setPreviewTemplate] = useState(null)
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateStatusFilter, setTemplateStatusFilter] = useState('ALL')
  const [copyFeedback, setCopyFeedback] = useState('')
  // Templates Meta por conexão: registros persistidos no master + ações
  const [templateRecords, setTemplateRecords] = useState([])
  const [currentWabaId, setCurrentWabaId] = useState(null)
  const [templateRecordsError, setTemplateRecordsError] = useState(null)
  const [syncingTemplates, setSyncingTemplates] = useState(false)
  const [templateRowBusyId, setTemplateRowBusyId] = useState(null)
  const [recommendedDialogOpen, setRecommendedDialogOpen] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  // "Como usar" (modelo aprovado -> snippet de disparo + teste) e "Excluir"
  const [howToUseTemplate, setHowToUseTemplate] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  // "Corrigir e reenviar": a linha em edição — abre o editor pré-preenchido e o
  // submit vai pro /edit do master (o template na Meta é atualizado, sem excluir).
  const [editTarget, setEditTarget] = useState(null)
  const containerRef = useRef(null)
  const animatedOnceRef = useRef(false)

  const fetchSnapshot = useCallback(async (force = false) => {
    if (!api || !secret) return
    try {
      if (force) setRefreshing(true)
      else setLoading(true)
      setError(null)
      const data = await hipersendService.notificacoesInteligentes.getWabaSnapshot(api, secret, { force })
      setResponse(data)
    } catch (err) {
      const apiMsg = err?.response?.data?.error || err?.response?.data?.message || err?.message
      setError(apiMsg || 'Falha ao consultar dados da Meta')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [api, secret])

  useEffect(() => {
    fetchSnapshot(false)
  }, [fetchSnapshot])

  // Registros locais de template desta conexão. Erro aqui NÃO derruba a tela:
  // o snapshot continua funcionando e mostramos só um aviso discreto na seção.
  const fetchTemplateRecords = useCallback(async () => {
    if (!api || !secret) return
    try {
      setTemplateRecordsError(null)
      const data = await hipersendService.metaTemplates.list(api, secret)
      setTemplateRecords(Array.isArray(data?.templates) ? data.templates : [])
      // A WABA da conexão vem junto da lista. Guardamos pra conseguir apontar,
      // linha a linha, o que pertence à conta ATUAL e o que ficou pra trás de
      // uma conta anterior — quando a conexão é refeita noutra WABA, os
      // registros da antiga continuam aqui e todos aparecem como "não
      // encontrado na Meta", sem nenhuma pista do porquê.
      setCurrentWabaId(data?.waba?.wabaId || null)
    } catch (err) {
      setTemplateRecordsError(describeApiError(err))
    }
  }, [api, secret])

  useEffect(() => {
    fetchTemplateRecords()
  }, [fetchTemplateRecords])

  // Substitui (ou insere) um registro devolvido por syncOne/retry sem recarregar tudo
  const upsertTemplateRecord = useCallback((updated) => {
    if (!updated?._id) return
    setTemplateRecords((prev) => (
      prev.some((r) => r._id === updated._id)
        ? prev.map((r) => (r._id === updated._id ? updated : r))
        : [...prev, updated]
    ))
  }, [])

  // Sincroniza todos os registros com a WABA e recarrega snapshot (force) + lista
  const handleSyncTemplates = useCallback(async () => {
    setSyncingTemplates(true)
    try {
      const result = await hipersendService.metaTemplates.sync(api, secret)
      await fetchSnapshot(true)
      await fetchTemplateRecords()
      const summary = result?.summary
      setCopyFeedback(summary
        ? `Sincronizado com a Meta: ${summary.updated} atualizados, ${summary.adopted} adotados, ${summary.missing} ausentes`
        : 'Modelos sincronizados com a Meta')
    } catch (err) {
      setCopyFeedback(describeApiError(err))
    } finally {
      setSyncingTemplates(false)
    }
  }, [api, secret, fetchSnapshot, fetchTemplateRecords])

  const handleSyncOneTemplate = useCallback(async (item) => {
    if (!item?._id) return
    setTemplateRowBusyId(item._id)
    try {
      const data = await hipersendService.metaTemplates.syncOne(api, secret, item._id)
      upsertTemplateRecord(data?.template)
      setCopyFeedback(`Modelo "${item.name}" sincronizado com a Meta`)
    } catch (err) {
      setCopyFeedback(describeApiError(err))
    } finally {
      setTemplateRowBusyId(null)
    }
  }, [api, secret, upsertTemplateRecord])

  const handleRetryTemplate = useCallback(async (item) => {
    if (!item?._id) return
    setTemplateRowBusyId(item._id)
    try {
      const data = await hipersendService.metaTemplates.retry(api, secret, item._id)
      upsertTemplateRecord(data?.template)
      setCopyFeedback(`Modelo "${item.name}" reenviado para análise da Meta`)
    } catch (err) {
      setCopyFeedback(describeApiError(err))
      // o registro pode ter ganho um lastError novo — recarrega o estado local
      fetchTemplateRecords()
    } finally {
      setTemplateRowBusyId(null)
    }
  }, [api, secret, upsertTemplateRecord, fetchTemplateRecords])

  // Exclui o modelo da WABA (Graph) e o registro local. Só faz sentido pra linha
  // com _id (gerenciada por nós); linha "somente Meta" não expõe o botão. Erro em
  // pt-br sem derrubar a tela; sucesso recarrega snapshot + registros.
  const handleDeleteTemplate = useCallback(async (item) => {
    if (!item?._id) return
    setTemplateRowBusyId(item._id)
    try {
      await hipersendService.metaTemplates.remove(api, secret, item._id)
      setCopyFeedback(`Modelo "${item.name}" excluído da WABA e do painel`)
      await fetchTemplateRecords()
      await fetchSnapshot(true)
    } catch (err) {
      setCopyFeedback(describeApiError(err))
    } finally {
      setTemplateRowBusyId(null)
    }
  }, [api, secret, fetchTemplateRecords, fetchSnapshot])

  const snapshot = response?.data || null
  const phoneNumber = snapshot?.phoneNumber || null
  const waba = snapshot?.waba || null
  const businessProfile = snapshot?.businessProfile || null
  // Veredito de saúde da conta (pagamento/elegibilidade), calculado no backend a
  // partir do health_status da Meta + falhas de envio recentes. Null = sem dados.
  const accountHealth = snapshot?.accountHealth || null
  const templates = useMemo(() => snapshot?.templates || [], [snapshot])

  const qualityRating = normalizeStatus(phoneNumber?.quality_rating) || 'UNKNOWN'
  const qualityColor = QUALITY_COLORS[qualityRating] || QUALITY_COLORS.UNKNOWN
  const qualityLabel = QUALITY_LABELS[qualityRating] || qualityRating

  const tierKey = normalizeTier(phoneNumber?.messaging_limit_tier)
  const tierLabel = tierKey
    ? (TIER_LABELS[tierKey] || `Nível ${tierKey.replace('TIER_', '')}`)
    : 'Aguardando classificação'
  const tierDescription = tierKey
    ? (TIER_DESCRIPTIONS[tierKey] || 'Limite definido pela Meta com base no histórico de envios')
    : 'A Meta classifica automaticamente seu número em 24-72h após os primeiros envios. Mantenha qualidade Excelente para subir mais rápido.'

  const codeKey = normalizeStatus(phoneNumber?.code_verification_status)
  const verificationLabel = codeKey
    ? (CODE_VERIFICATION_LABELS[codeKey] || codeKey)
    : 'PIN pendente'
  const verificationColor = codeKey
    ? (CODE_VERIFICATION_COLORS[codeKey] || QUALITY_COLORS.UNKNOWN)
    : QUALITY_COLORS.YELLOW

  const nameStatusKey = normalizeStatus(phoneNumber?.name_status)
  const nameStatusLabel = nameStatusKey ? (NAME_STATUS_LABELS[nameStatusKey] || nameStatusKey) : null
  const nameStatusColor = nameStatusKey ? (NAME_STATUS_COLORS[nameStatusKey] || QUALITY_COLORS.UNKNOWN) : QUALITY_COLORS.UNKNOWN

  const businessKey = normalizeStatus(waba?.business_verification_status)
  const businessLabel = businessKey ? (BUSINESS_VERIFICATION_LABELS[businessKey] || businessKey) : '—'
  const businessColor = businessKey ? (BUSINESS_VERIFICATION_COLORS[businessKey] || QUALITY_COLORS.UNKNOWN) : QUALITY_COLORS.UNKNOWN

  const { score: healthScore, factors: healthFactors } = useMemo(
    () => computeHealthScore({ phoneNumber, waba }),
    [phoneNumber, waba]
  )
  const healthMeta = getHealthLabel(healthScore)

  const tierProgress = useMemo(() => {
    if (!tierKey) return null
    const tiers = ['TIER_50', 'TIER_250', 'TIER_1K', 'TIER_10K', 'TIER_100K', 'TIER_UNLIMITED']
    const idx = tiers.indexOf(tierKey)
    if (idx < 0) return null
    return Math.round(((idx + 1) / tiers.length) * 100)
  }, [tierKey])

  // Linhas da seção de modelos: registros persistidos + templates do snapshot
  // mesclados por nome+idioma (o registro vence; o snapshot completa).
  const mergedTemplates = useMemo(
    () => mergeTemplates(templateRecords, templates),
    [templateRecords, templates]
  )

  // Próximos passos usam a visão mesclada (registros locais + snapshot) — a
  // mesma da lista logo abaixo. Só o snapshot (cacheado) ficaria pra trás logo
  // após instalar templates, mostrando contagens contraditórias na mesma tela.
  const nextSteps = useMemo(
    () => computeNextSteps({ phoneNumber, waba, templates: mergedTemplates }),
    [phoneNumber, waba, mergedTemplates]
  )

  const mergedTemplatesByStatus = useMemo(() => {
    const groups = { APPROVED: 0, PENDING: 0, REJECTED: 0, OTHER: 0 }
    for (const t of mergedTemplates) {
      const key = normalizeStatus(t.status)
      if (groups[key] != null) groups[key]++
      else groups.OTHER++
    }
    return groups
  }, [mergedTemplates])

  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase()
    return mergedTemplates.filter(t => {
      if (templateStatusFilter !== 'ALL' && normalizeStatus(t.status) !== templateStatusFilter) return false
      if (!q) return true
      const haystack = `${t.name || ''} ${t.language || ''} ${t.category || ''}`.toLowerCase()
      return haystack.includes(q)
    })
  }, [mergedTemplates, templateSearch, templateStatusFilter])

  const handleCopyTemplateTag = useCallback(async (template) => {
    const tag = `%WhatsappCloudApi:${template.name}%`
    try {
      await navigator.clipboard.writeText(tag)
      setCopyFeedback(`Tag ${tag} copiada`)
    } catch (e) {
      setCopyFeedback('Não foi possível copiar — copie manualmente: ' + tag)
    }
  }, [])

  useEffect(() => {
    if (!snapshot || animatedOnceRef.current || !containerRef.current) return
    const cards = containerRef.current.querySelectorAll('[data-anim-card]')
    if (cards.length === 0) return
    anime({
      targets: cards,
      opacity: [0, 1],
      translateY: [16, 0],
      delay: anime.stagger(80),
      duration: 600,
      easing: 'easeOutCubic',
    })
    animatedOnceRef.current = true
  }, [snapshot])

  if (loading) {
    return <SnapshotSkeleton />
  }

  if (error && !snapshot) {
    return (
      <Paper elevation={2}>
        <div className="p-6">
          <div className="flex items-start gap-3">
            <ErrorOutlineIcon style={{ color: '#ef4444', fontSize: 28 }} />
            <div className="flex-1">
              <Text className="font-black">Não foi possível carregar dados da Meta</Text>
              <Text className="text-sm font-light mt-1 opacity-90">{error}</Text>
              <div className="mt-3">
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchSnapshot(true)}
                >
                  Tentar novamente
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Paper>
    )
  }

  if (!snapshot) return null

  return (
    <div ref={containerRef} className="space-y-4">
      {/* Aviso de carregamento parcial — fica no topo pra ser a primeira coisa que o cliente vê */}
      {snapshot.errors && Object.values(snapshot.errors).some(Boolean) && (
        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={0} sx={{ backgroundColor: 'rgba(239,68,68,0.08)', borderLeft: '3px solid #ef4444' }}>
            <div className="p-4 flex items-start gap-2">
              <ErrorOutlineIcon style={{ color: '#ef4444', fontSize: 20 }} />
              <div className="flex-1">
                <Text className="text-sm font-bold" style={{ color: '#ef4444' }}>
                  Alguns dados não puderam ser carregados
                </Text>
                <Text className="text-xs font-light opacity-80 mt-1">
                  Isso pode acontecer quando o token de acesso tem permissões limitadas. Os demais dados continuam atualizados.
                </Text>
                <ul className="mt-2 text-xs font-light list-disc list-inside opacity-80">
                  {snapshot.errors.phoneNumber && <li>Dados do número: {snapshot.errors.phoneNumber}</li>}
                  {snapshot.errors.waba && <li>Dados da conta WhatsApp Business: {snapshot.errors.waba}</li>}
                  {snapshot.errors.templates && <li>Modelos de mensagem: {snapshot.errors.templates}</li>}
                  {snapshot.errors.businessProfile && <li>Perfil do negócio: {snapshot.errors.businessProfile}</li>}
                </ul>
              </div>
            </div>
          </Paper>
        </div>
      )}

      <div className="flex items-end justify-between flex-wrap gap-3" data-anim-card style={{ opacity: 0 }}>
        <div>
          <Text className="text-xl font-black">Dados da sua conta Meta Oficial</Text>
          <Text className="text-xs font-light opacity-70">
            {response?.updatedAt && `Última sincronização ${moment(response.updatedAt).fromNow()}`}
            {response?.stale && ' • dados em cache (Meta indisponível agora)'}
          </Text>
        </div>
        <Button
          variant="text"
          size="small"
          startIcon={<RefreshIcon className={refreshing ? 'animate-spin' : ''} />}
          onClick={() => fetchSnapshot(true)}
          disabled={refreshing}
        >
          {refreshing ? 'Atualizando...' : 'Sincronizar agora'}
        </Button>
      </div>

      {/* Alerta de conta (pagamento/elegibilidade). Só aparece quando a Meta
          está recusando os envios — o cliente descobre AQUI, e não pelas
          mensagens que somem em silêncio. */}
      <MetaAccountHealthBanner accountHealth={accountHealth} data-anim-card />

      {/* Saúde Geral + Perfil do Negócio lado a lado */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-anim-card style={{ opacity: 0 }} className="md:col-span-1">
          <Paper elevation={3} sx={{ height: '100%', borderTop: `4px solid ${healthMeta.color}` }}>
            <div className="p-5 h-full flex flex-col">
              <CardTitle
                icon={FavoriteIcon}
                label="Saúde da conta"
                color={healthMeta.color}
                tooltip={HEALTH_SCORE_TOOLTIP}
              />
              <div className="flex items-center gap-4 mt-1">
                <ScoreRing score={healthScore} color={healthMeta.color} />
                <div className="flex-1 min-w-0">
                  <Text className="text-xl font-black" style={{ color: healthMeta.color }}>
                    {healthMeta.label}
                  </Text>
                  <Text className="text-xs font-light opacity-90 mt-1 leading-snug">
                    {healthMeta.description}
                  </Text>
                </div>
              </div>
              <div className="mt-4 space-y-1.5">
                {healthFactors.map(f => (
                  <div key={f.label} className="flex items-center gap-2">
                    <Text className="text-[11px] font-light opacity-70 flex-1 truncate">{f.label}</Text>
                    <div className="w-16 h-1.5 rounded-full bg-gray-500 bg-opacity-20 overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${f.value}%`,
                          backgroundColor: f.value >= 70 ? QUALITY_COLORS.GREEN : f.value >= 40 ? QUALITY_COLORS.YELLOW : QUALITY_COLORS.RED,
                          transition: 'width 1s ease-out',
                        }}
                      />
                    </div>
                    <Text className="text-[11px] font-bold w-8 text-right">
                      <AnimatedNumber value={f.value} />
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }} className="md:col-span-2">
          <Paper elevation={3} sx={{ height: '100%' }}>
            <div className="p-5 h-full">
              <CardTitle
                icon={StorefrontIcon}
                label="Perfil do negócio"
                color={theme.palette.primary.main}
                tooltip={
                  <div className="text-xs leading-relaxed">
                    <div className="font-bold mb-1">Perfil business</div>
                    <div>Dados que aparecem para seus clientes no perfil do WhatsApp. Configure pelo Business Manager para passar mais credibilidade.</div>
                  </div>
                }
              />
              <div className="flex items-start gap-4">
                <Avatar
                  src={businessProfile?.profile_picture_url || undefined}
                  sx={{ width: 72, height: 72, backgroundColor: theme.palette.primary.main }}
                >
                  {(phoneNumber?.verified_name || 'M').charAt(0).toUpperCase()}
                </Avatar>
                <div className="flex-1 min-w-0">
                  <Text className="text-lg font-black truncate">
                    {phoneNumber?.verified_name || 'Sem nome verificado'}
                  </Text>
                  <Text className="text-sm font-light opacity-90">
                    {phoneNumber?.display_phone_number || '—'}
                  </Text>
                  {businessProfile?.about && (
                    <Text className="text-sm font-light mt-2 leading-snug opacity-90">
                      {businessProfile.about}
                    </Text>
                  )}
                  {!businessProfile?.about && (
                    <Text className="text-xs font-light italic mt-2 opacity-60">
                      Adicione uma descrição "Sobre" pelo Business Manager para passar credibilidade.
                    </Text>
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {businessProfile?.vertical && (
                  <div className="flex items-center gap-2">
                    <BusinessIcon style={{ fontSize: 16, opacity: 0.7 }} />
                    <Text className="text-xs font-light">
                      {VERTICAL_LABELS[businessProfile.vertical] || businessProfile.vertical}
                    </Text>
                  </div>
                )}
                {businessProfile?.email && (
                  <div className="flex items-center gap-2">
                    <EmailIcon style={{ fontSize: 16, opacity: 0.7 }} />
                    <Text className="text-xs font-light truncate">{businessProfile.email}</Text>
                  </div>
                )}
                {businessProfile?.address && (
                  <div className="flex items-center gap-2 sm:col-span-2">
                    <LocationOnIcon style={{ fontSize: 16, opacity: 0.7 }} />
                    <Text className="text-xs font-light truncate">{businessProfile.address}</Text>
                  </div>
                )}
                {Array.isArray(businessProfile?.websites) && businessProfile.websites.length > 0 && (
                  <div className="flex items-center gap-2 sm:col-span-2 flex-wrap">
                    <LanguageIcon style={{ fontSize: 16, opacity: 0.7 }} />
                    {businessProfile.websites.map((w, i) => (
                      <a
                        key={i}
                        href={w}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-light underline hover:opacity-80"
                        style={{ color: theme.palette.primary.main }}
                      >
                        {w.replace(/^https?:\/\//, '')}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Paper>
        </div>
      </div>

      {/* 6 cards de status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={WhatsAppIcon}
                label="Número"
                color={theme.palette.primary.main}
                tooltip={NUMBER_TOOLTIP}
              />
              <Text className="text-lg font-black">
                {phoneNumber?.display_phone_number || '—'}
              </Text>
              <Text className="text-sm font-light opacity-90 mt-0.5">
                {phoneNumber?.verified_name || 'Sem nome verificado'}
              </Text>
              {nameStatusLabel && (
                <div className="mt-2">
                  <StatusPill
                    color={nameStatusColor}
                    label={nameStatusLabel}
                    tooltip={NAME_STATUS_TOOLTIP}
                  />
                </div>
              )}
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={VerifiedIcon}
                label="Qualidade do número"
                color={qualityColor}
                tooltip={QUALITY_TOOLTIP}
              />
              <div className="flex items-center gap-2">
                <span
                  className="w-[14px] h-[14px] rounded-full"
                  style={{ backgroundColor: qualityColor, animation: qualityRating === 'RED' || qualityRating === 'YELLOW' ? 'pulse 2s infinite' : 'none' }}
                />
                <Text className="text-lg font-black" style={{ color: qualityColor }}>
                  {qualityLabel}
                </Text>
              </div>
              <Text className="text-xs font-light opacity-70 mt-2 leading-snug">
                Reflete a satisfação dos clientes com suas mensagens. Excelente é o ideal; Crítica restringe envios.
              </Text>
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={SpeedIcon}
                label="Limite de mensagens"
                color={theme.palette.primary.main}
                tooltip={TIER_TOOLTIP}
              />
              <Text
                className={tierKey ? 'text-lg font-black' : 'text-base font-bold opacity-80'}
              >
                {tierLabel}
              </Text>
              <Text className="text-xs font-light opacity-90 mt-1 leading-snug">
                {tierDescription}
              </Text>
              {tierProgress != null && (
                <div className="mt-3">
                  <div className="w-full h-1.5 rounded-full bg-gray-500 bg-opacity-20 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${tierProgress}%`,
                        backgroundColor: theme.palette.primary.main,
                        transition: 'width 1.2s ease-out',
                      }}
                    />
                  </div>
                  <Text className="text-[10px] font-light opacity-60 mt-1">
                    Progressão para o nível máximo
                  </Text>
                </div>
              )}
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={BusinessIcon}
                label="Verificação do negócio"
                color={businessColor}
                tooltip={BUSINESS_VERIFICATION_TOOLTIP}
              />
              <Text className="text-lg font-black" style={{ color: businessColor }}>
                {businessLabel}
              </Text>
              <Text className="text-sm font-light opacity-90 mt-0.5">
                {waba?.name || 'Conta WhatsApp Business'}
              </Text>
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={LockOutlinedIcon}
                label="PIN de segurança"
                color={verificationColor}
                tooltip={CODE_VERIFICATION_TOOLTIP}
              />
              <Text className="text-lg font-black" style={{ color: verificationColor }}>
                {verificationLabel}
              </Text>
              <Text className="text-xs font-light opacity-70 mt-1 leading-snug">
                Camada extra de segurança no re-registro. Não afeta envios.
              </Text>
            </div>
          </Paper>
        </div>

        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={DescriptionIcon}
                label="Modelos de mensagem"
                color={theme.palette.primary.main}
                tooltip={TEMPLATES_TOOLTIP}
              />
              {/* Contagens da visão mesclada (registros locais + snapshot) — a
                  mesma fonte da lista de modelos, pra tela nunca se contradizer */}
              <Text className="text-lg font-black">
                <AnimatedNumber value={mergedTemplates.length} />
              </Text>
              <div className="flex flex-wrap gap-1 mt-2">
                {mergedTemplatesByStatus.APPROVED > 0 && (
                  <Chip
                    size="small"
                    label={`${mergedTemplatesByStatus.APPROVED} aprovados`}
                    sx={{ backgroundColor: `${TEMPLATE_STATUS_COLORS.APPROVED}1F`, color: TEMPLATE_STATUS_COLORS.APPROVED, fontWeight: 700 }}
                  />
                )}
                {mergedTemplatesByStatus.PENDING > 0 && (
                  <Chip
                    size="small"
                    label={`${mergedTemplatesByStatus.PENDING} em análise`}
                    sx={{ backgroundColor: `${TEMPLATE_STATUS_COLORS.PENDING}1F`, color: TEMPLATE_STATUS_COLORS.PENDING, fontWeight: 700 }}
                  />
                )}
                {mergedTemplatesByStatus.REJECTED > 0 && (
                  <Chip
                    size="small"
                    label={`${mergedTemplatesByStatus.REJECTED} rejeitados`}
                    sx={{ backgroundColor: `${TEMPLATE_STATUS_COLORS.REJECTED}1F`, color: TEMPLATE_STATUS_COLORS.REJECTED, fontWeight: 700 }}
                  />
                )}
                {mergedTemplates.length === 0 && (
                  <Text className="text-xs font-light opacity-70">
                    Nenhum modelo cadastrado ainda
                  </Text>
                )}
              </div>
            </div>
          </Paper>
        </div>
      </div>

      {/* Módulo de conexão (diagrama Meta compacto) — entre os cards de status e os próximos passos */}
      {connectionSlot && (
        <div data-anim-card style={{ opacity: 0 }}>
          {connectionSlot}
        </div>
      )}

      {/* Próximos passos */}
      {nextSteps.length > 0 && (
        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2}>
            <div className="p-5">
              <CardTitle
                icon={LightbulbIcon}
                label="Próximos passos sugeridos"
                color={theme.palette.primary.main}
              />
              <div className="space-y-3 mt-2">
                {nextSteps.map((step, idx) => {
                  const severityColor =
                    step.severity === 'high' ? QUALITY_COLORS.RED :
                    step.severity === 'medium' ? QUALITY_COLORS.YELLOW :
                    step.severity === 'info' ? theme.palette.primary.main :
                    QUALITY_COLORS.UNKNOWN
                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-3 p-3 rounded-md"
                      style={{ backgroundColor: `${severityColor}10`, borderLeft: `3px solid ${severityColor}` }}
                    >
                      <ArrowForwardIcon style={{ color: severityColor, fontSize: 18, marginTop: 2 }} />
                      <div className="flex-1">
                        <Text className="text-sm font-bold" style={{ color: severityColor }}>
                          {step.title}
                        </Text>
                        <Text className="text-xs font-light opacity-90 mt-1 leading-relaxed">
                          {step.description}
                        </Text>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </Paper>
        </div>
      )}

      {nextSteps.length === 0 && healthScore >= 90 && (
        <div data-anim-card style={{ opacity: 0 }}>
          <Paper elevation={2} sx={{ backgroundColor: `${QUALITY_COLORS.GREEN}0F` }}>
            <div className="p-5 flex items-center gap-3">
              <CheckCircleIcon style={{ color: QUALITY_COLORS.GREEN, fontSize: 32 }} />
              <div>
                <Text className="font-black" style={{ color: QUALITY_COLORS.GREEN }}>
                  Sua conta está em dia
                </Text>
                <Text className="text-xs font-light opacity-90 mt-0.5">
                  Nenhuma ação pendente. Continue mantendo a qualidade dos envios.
                </Text>
              </div>
            </div>
          </Paper>
        </div>
      )}

      {/* Seção de modelos: sempre visível no modo Meta (mesmo sem nenhum template,
          o cliente precisa dos botões pra instalar/criar o primeiro) */}
      <div data-anim-card style={{ opacity: 0 }}>
        <Paper elevation={2}>
          <div className="p-5">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setTemplatesExpanded(v => !v)}
              >
                <div className="flex items-center gap-2">
                  <DescriptionIcon style={{ color: theme.palette.primary.main, fontSize: 22 }} />
                  <Text className="text-lg font-black">Modelos de mensagem cadastrados</Text>
                </div>
                <IconButton size="small">
                  {templatesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </div>

              {/* Barra de ações dos templates — fora do Collapse pra ficar sempre à mão */}
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoAwesomeIcon style={{ fontSize: 16 }} />}
                  onClick={() => setRecommendedDialogOpen(true)}
                >
                  Modelos recomendados
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon style={{ fontSize: 16 }} />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Criar template
                </Button>
                <Tooltip title="Reconcilia os modelos desta conexão com o que está na Meta agora" arrow>
                  <span>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<SyncIcon style={{ fontSize: 16 }} className={syncingTemplates ? 'animate-spin' : ''} />}
                      onClick={handleSyncTemplates}
                      disabled={syncingTemplates}
                    >
                      {syncingTemplates ? 'Sincronizando...' : 'Sincronizar'}
                    </Button>
                  </span>
                </Tooltip>
                {templateRecordsError && (
                  <Tooltip title={templateRecordsError} arrow>
                    <span className="inline-flex items-center gap-1 cursor-help">
                      <ErrorOutlineIcon style={{ fontSize: 14, color: QUALITY_COLORS.YELLOW }} />
                      <Text className="text-xs font-light" style={{ color: QUALITY_COLORS.YELLOW }}>
                        estado local dos modelos indisponível
                      </Text>
                    </span>
                  </Tooltip>
                )}
              </div>

              <Collapse in={templatesExpanded}>
                <div className="mt-4 flex items-center gap-3 flex-wrap">
                  <TextField
                    size="small"
                    placeholder="Buscar por nome, idioma ou categoria"
                    value={templateSearch}
                    onChange={(e) => setTemplateSearch(e.target.value)}
                    sx={{ minWidth: 260, flex: 1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ fontSize: 18, opacity: 0.6 }} />
                        </InputAdornment>
                      ),
                      endAdornment: templateSearch ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setTemplateSearch('')}
                            sx={{ opacity: 0.6 }}
                          >
                            <ClearIcon style={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {[
                      { key: 'ALL', label: 'Todos', count: mergedTemplates.length },
                      { key: 'APPROVED', label: 'Aprovados', count: mergedTemplatesByStatus.APPROVED, color: TEMPLATE_STATUS_COLORS.APPROVED },
                      { key: 'PENDING', label: 'Em análise', count: mergedTemplatesByStatus.PENDING, color: TEMPLATE_STATUS_COLORS.PENDING },
                      { key: 'REJECTED', label: 'Rejeitados', count: mergedTemplatesByStatus.REJECTED, color: TEMPLATE_STATUS_COLORS.REJECTED },
                    ].map(f => {
                      const active = templateStatusFilter === f.key
                      return (
                        <Chip
                          key={f.key}
                          label={`${f.label} (${f.count})`}
                          size="small"
                          onClick={() => setTemplateStatusFilter(f.key)}
                          variant={active ? 'filled' : 'outlined'}
                          sx={{
                            fontWeight: 700,
                            cursor: 'pointer',
                            backgroundColor: active ? (f.color || theme.palette.primary.main) : 'transparent',
                            color: active ? '#fff' : (f.color || 'inherit'),
                            borderColor: f.color || theme.palette.primary.main,
                            '&:hover': {
                              backgroundColor: active ? (f.color || theme.palette.primary.main) : `${f.color || theme.palette.primary.main}1F`,
                            },
                          }}
                        />
                      )
                    })}
                  </div>
                </div>
                {filteredTemplates.length === 0 ? (
                  <div className="mt-6 text-center py-8 rounded-md" style={{ backgroundColor: 'rgba(128,128,128,0.05)' }}>
                    {mergedTemplates.length === 0 ? (
                      <>
                        <Text className="text-sm font-bold opacity-70">Nenhum modelo cadastrado ainda</Text>
                        <Text className="text-xs font-light opacity-60 mt-1">
                          Comece pelos <b>Modelos recomendados</b> ou clique em <b>Criar template</b> logo acima
                        </Text>
                      </>
                    ) : (
                      <>
                        <Text className="text-sm font-bold opacity-70">Nenhum modelo encontrado</Text>
                        <Text className="text-xs font-light opacity-60 mt-1">
                          Ajuste o filtro ou a busca para ver outros resultados
                        </Text>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 overflow-x-auto">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><Text className="text-xs font-bold uppercase opacity-70">Nome</Text></TableCell>
                          <TableCell><Text className="text-xs font-bold uppercase opacity-70">Idioma</Text></TableCell>
                          <TableCell><Text className="text-xs font-bold uppercase opacity-70">Categoria</Text></TableCell>
                          <TableCell><Text className="text-xs font-bold uppercase opacity-70">Status</Text></TableCell>
                          <TableCell align="right"><Text className="text-xs font-bold uppercase opacity-70">Ações</Text></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTemplates.map((t, idx) => {
                          const statusKey = normalizeStatus(t.status)
                          const categoryKey = normalizeStatus(t.category)
                          const hasComponents = Array.isArray(t.components) && t.components.length > 0
                          const isApproved = statusKey === 'APPROVED'
                          const localBusy = t._id != null && templateRowBusyId === t._id
                          const isSubmitting = t.localProcessingStatus === 'submitting'
                          const isFailed = t.localProcessingStatus === 'failed'
                          const localStatusInfo = (isSubmitting || isFailed) ? statusInfo(t) : null
                          const deOutraConta = isOrphanFromAnotherWaba(t, currentWabaId)
                          const rejectionTooltip = statusKey === 'REJECTED' && t.rejectionReason ? (
                            <div className="text-xs leading-relaxed">
                              <div className="font-bold mb-1">Motivo informado pela Meta</div>
                              <div>{t.rejectionReason}</div>
                            </div>
                          ) : null
                          return (
                            <TableRow key={`${t.name}-${t.language}-${idx}`}>
                              <TableCell><Text className="text-sm font-medium">{t.name}</Text></TableCell>
                              <TableCell><Text className="text-sm font-light">{t.language}</Text></TableCell>
                              <TableCell>
                                <Text className="text-sm font-light">
                                  {TEMPLATE_CATEGORY_LABELS[categoryKey] || t.category || '—'}
                                </Text>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {(statusKey || !localStatusInfo) && (
                                    <StatusPill
                                      color={TEMPLATE_STATUS_COLORS[statusKey] || QUALITY_COLORS.UNKNOWN}
                                      label={TEMPLATE_STATUS_LABELS[statusKey] || t.status || '—'}
                                      tooltip={rejectionTooltip}
                                    />
                                  )}
                                  {localStatusInfo && (
                                    <Tooltip
                                      title={isFailed && t.lastError ? `Último erro: ${t.lastError}` : ''}
                                      arrow
                                    >
                                      <Chip
                                        size="small"
                                        label={localStatusInfo.label}
                                        sx={{
                                          height: 20,
                                          fontSize: 11,
                                          fontWeight: 700,
                                          backgroundColor: `${localStatusInfo.color}1F`,
                                          color: localStatusInfo.color,
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                  {deOutraConta && (
                                    <Tooltip
                                      title="Este modelo foi criado quando a conexão usava outra conta do WhatsApp Business e não existe na conta atual. Use Recriar na Meta para criá-lo aqui."
                                      arrow
                                    >
                                      <Chip
                                        size="small"
                                        label="Outra conta"
                                        sx={{
                                          height: 20,
                                          fontSize: 11,
                                          fontWeight: 700,
                                          backgroundColor: `${QUALITY_COLORS.UNKNOWN}1F`,
                                          color: QUALITY_COLORS.UNKNOWN,
                                        }}
                                      />
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell align="right">
                                <div className="inline-flex items-center gap-1 justify-end">
                                  {t._id && canRetry(t) && (
                                    <Tooltip
                                      title={
                                        t.metaReviewStatus === 'NOT_FOUND'
                                          ? 'Recriar na Meta: este modelo não existe mais lá (foi apagado no gerenciador ou ficou numa conta anterior). Reenvia a definição guardada aqui e cria de novo na conta atual.'
                                          : 'Tentar novamente: reenvia este modelo para análise da Meta'
                                      }
                                      arrow
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={localBusy}
                                          onClick={() => handleRetryTemplate(t)}
                                        >
                                          <ReplayIcon style={{ fontSize: 16 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {canEdit(t) && (
                                    <Tooltip
                                      title={
                                        String(t.metaReviewStatus || '').toUpperCase() === 'REJECTED'
                                          ? 'Corrigir e reenviar: edite o conteúdo e envie de novo para análise, sem excluir o modelo'
                                          : 'Editar o conteúdo deste modelo (ele volta para análise da Meta)'
                                      }
                                      arrow
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={localBusy}
                                          onClick={() => setEditTarget(t)}
                                        >
                                          <EditOutlinedIcon style={{ fontSize: 16 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  {t._id && (
                                    <Tooltip title="Sincronizar este modelo com a Meta" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          disabled={localBusy}
                                          onClick={() => handleSyncOneTemplate(t)}
                                        >
                                          <SyncIcon
                                            style={{ fontSize: 16 }}
                                            className={localBusy ? 'animate-spin' : ''}
                                          />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    title={hasComponents ? 'Ver como a mensagem chega ao cliente' : 'Conteúdo ainda não sincronizado — clique pra atualizar'}
                                    arrow
                                  >
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      startIcon={<VisibilityIcon style={{ fontSize: 14 }} />}
                                      onClick={() => setPreviewTemplate(t)}
                                      sx={{ minWidth: 0, py: 0.5, px: 1, fontSize: 11 }}
                                    >
                                      Preview
                                    </Button>
                                  </Tooltip>
                                  {isApproved && (
                                    <Tooltip title="Ver o código de disparo pronto e testar o envio" arrow>
                                      <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<IntegrationInstructionsIcon style={{ fontSize: 14 }} />}
                                        onClick={() => setHowToUseTemplate(t)}
                                        sx={{ minWidth: 0, py: 0.5, px: 1, fontSize: 11 }}
                                      >
                                        Como usar
                                      </Button>
                                    </Tooltip>
                                  )}
                                  <Tooltip
                                    title={
                                      isApproved
                                        ? `Copiar %WhatsappCloudApi:${t.name}% para usar no ERP/template`
                                        : 'Só modelos aprovados podem ser enviados'
                                    }
                                    arrow
                                  >
                                    <span>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        disabled={!isApproved}
                                        startIcon={<ContentCopyIcon style={{ fontSize: 14 }} />}
                                        onClick={() => handleCopyTemplateTag(t)}
                                        sx={{ minWidth: 0, py: 0.5, px: 1, fontSize: 11 }}
                                      >
                                        Copiar tag
                                      </Button>
                                    </span>
                                  </Tooltip>
                                  {t._id && (
                                    <Tooltip title="Excluir este modelo da WABA e do painel" arrow>
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          disabled={localBusy}
                                          onClick={() => setDeleteTarget(t)}
                                        >
                                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </Collapse>
          </div>
        </Paper>
      </div>

      <RecommendedTemplatesDialog
        open={recommendedDialogOpen}
        onClose={() => setRecommendedDialogOpen(false)}
        api={api}
        secret={secret}
        onInstalled={(message) => {
          fetchTemplateRecords()
          if (message) setCopyFeedback(message)
        }}
      />

      <CreateTemplateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        api={api}
        secret={secret}
        onInstalled={(message) => {
          fetchTemplateRecords()
          if (message) setCopyFeedback(message)
        }}
      />

      {/* Mesmo editor, em MODO EDIÇÃO: abre pré-preenchido com o conteúdo atual
          (inclusive o cabeçalho) e o submit atualiza o template na Meta via
          /edit — sem excluir, preservando o id e o histórico. */}
      <CreateTemplateDialog
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        api={api}
        secret={secret}
        title="Corrigir e reenviar"
        editTarget={editTarget}
        initialForm={editTarget ? componentsToForm(editTarget.components, {
          name: editTarget.name,
          language: editTarget.language,
          category: editTarget.category,
        }) : null}
        onInstalled={(message) => {
          fetchTemplateRecords()
          if (message) setCopyFeedback(message)
        }}
      />

      <TemplatePreviewDialog
        template={previewTemplate}
        open={!!previewTemplate}
        onClose={() => setPreviewTemplate(null)}
        onRefresh={() => fetchSnapshot(true)}
      />

      <HowToUseTemplateDialog
        open={!!howToUseTemplate}
        item={howToUseTemplate}
        api={api}
        secret={secret}
        onClose={() => setHowToUseTemplate(null)}
      />

      {/* Confirmação de exclusão — deixa claro que remove da WABA e do painel */}
      <ConfirmationModal
        open={!!deleteTarget}
        title={
          <div className="flex items-center gap-2">
            <DeleteOutlineIcon style={{ color: QUALITY_COLORS.RED }} />
            <Text component="span">Excluir modelo</Text>
          </div>
        }
        challengeQuestion="Esta ação é permanente e não pode ser desfeita."
        challengeAwnswer="EXCLUIR"
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          const target = deleteTarget
          setDeleteTarget(null)
          handleDeleteTemplate(target)
        }}
      >
        <Text className="text-base font-medium">
          Deseja realmente excluir o modelo "{deleteTarget?.name}"?
        </Text>
        <Text className="mt-1">
          Ele será removido da sua conta WhatsApp Business (Meta) e do painel. Os
          envios que dependem deste modelo deixam de funcionar.
        </Text>
      </ConfirmationModal>

      <Snackbar
        open={!!copyFeedback}
        autoHideDuration={2400}
        onClose={() => setCopyFeedback('')}
        message={copyFeedback}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  )
}

export default MetaWabaSnapshot
